import {
  Box,
  Button,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Table,
  TextField,
  Grid,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Collapse } from "@mui/material";
import { formatDate } from "../../utils/formatDate";

const EmailDelivery = ({ device }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [smsList, setSmsList] = useState([]);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [searchQuery, setSearchQuery] = useState("");

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        "date",
        "Modem Serial Number",
        "Phone",
        "Text",
        "Status",
        "Length",
        "Carrier Name",
      ],
      ...smsList.map((sms) => [
        sms.date,
        sms.unitId,
        sms.to,
        sms.text,
        sms.status,
        sms.length,
        sms.carrierName,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `units_Management_data.csv`;
    link.click();

    handleExportMenuClose();
    toast.success(`Exported units Management data view to CSV`);
  };

  const exportToExcel = () => {
    let headers, rows;
    headers = [
      "Unit Name",
      "Modem Serial Number",
      "Phone",
      "Text",
      "Status",
      "Length",
      "Carrier Name",
    ];
    rows = smsList.map((sms) => [
      sms.date,
      sms.unitId,
      sms.to,
      sms.text,
      sms.status,
      sms.length,
      sms.carrierName,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Units");
    XLSX.writeFile(workbook, `units_Management_data.xlsx`);

    handleExportMenuClose();
    toast.success(`Exported units Management data view to Excel`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Units Report", 14, 20);

    let headers, rows;

    headers = [
      [
        "Unit Name",
        "Modem Serial Number",
        "Phone",
        "Text",
        "Status",
        "Length",
        "Carrier Name",
      ],
    ];
    rows = smsList.map((sms) => [
      sms.date,
      sms.unitId,
      sms.to,
      sms.text,
      sms.status,
      sms.length,
      sms.carrierName,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`units_Management_data.pdf`);
    handleExportMenuClose();
    toast.success(`Exported units Management data view to PDF`);
  };

  const copyToClipboard = () => {
    const textContent = smsList
      .map(
        (sms) =>
          `${sms.date}\t${sms.unitId}\t${sms.to}\t${sms.text}\t${sms.status}\t${sms.length}\t${sms.carrierName}`,
      )
      .join("\n");

    const headers =
      "Unit Name\tModem Serial Number\tPhone\tText\tStatus\tLength\tCarrier Name";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success("Data copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const fetchSMS = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/alarms/sms?unitId=${device.id}`,
      );
      setSmsList(response.data || []);
    } catch (err) {
      console.error("Error fetching sms:", err);
      setError("Failed to load sms.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSMS();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedSmsData = useMemo(() => {
    if (!smsList) return [];

    return [...smsList].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Message ID":
          aValue = a.id;
          bValue = b.id;
          break;
        case "Date":
          aValue = a.date;
          bValue = b.date;
          break;
        case "Unit ID":
          aValue = a.unitId;
          bValue = b.unitId;
          break;
        case "Phone":
          aValue = a.to;
          bValue = b.to;
          break;
        case "Text":
          aValue = a.text;
          bValue = b.text;
          break;
        case "Status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "Length":
          aValue = a.length;
          bValue = b.length;
          break;
        case "Carrier Name":
          aValue = a.carrierName;
          bValue = b.carrierName;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [smsList, order, orderBy]);

  const filteredEmailData = []

  const filteredSMSData = sortedSmsData.filter((row) => {
    const formatDate = new Date(row.date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .toLowerCase();

    const searchTerm = searchQuery.toLowerCase();

    return (
      formatDate.includes(searchTerm) ||
      (row.id && row.id.toLowerCase().includes(searchTerm)) || // Check if row.to exists
      (row.to && row.to.toLowerCase().includes(searchTerm)) || // Check if row.to exists
      (row.text && row.text.toLowerCase().includes(searchTerm)) || // Check if row.text exists
      (row.length &&
        row.length.toString().toLowerCase().includes(searchTerm)) || // Check if length exists
      (row.status && row.status.toLowerCase().includes(searchTerm)) // Check if row.status exists
    );
  });

  const renderUnitsTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: "63vh",
        width: "100%",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {/* Mobile expand button column */}
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "10.5px",

                width: "1%", // 🔥 key trick — shrink to content
                whiteSpace: "nowrap", // prevent stretching
                padding: "6px 4px", // less padding = less width
              }}
            >
              Details
            </TableCell>
            {[
              { label: "Message ID", showOnXs: false },
              { label: "Date", showOnXs: false },
              { label: "Unit ID", showOnXs: true },
              { label: "Phone", showOnXs: true },
              { label: "Text", showOnXs: false },
              { label: "Status", showOnXs: true },
              { label: "Length", showOnXs: false },
              { label: "Carrier Name", showOnXs: false },
            ].map((col) => (
              <TableCell
                key={col.label}
                sx={{
                  fontSize: { xs: "12px", sm: "15px", md: "16px" },
                  display: {
                    textAlign: "center",
                    xs: col.showOnXs ? "table-cell" : "none",
                    md: "table-cell",
                  },
                }}
              >
                <TableSortLabel
                  active={orderBy === col.label}
                  direction={orderBy === col.label ? order : "asc"}
                  onClick={() => handleRequestSort(col.label)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEmailData.length > 0 ? (
            filteredEmailData.map((sms, index) => (
              <React.Fragment key={sms.id}>
                <TableRow
                  key={sms.id}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  {/* Expand Button - Mobile Only */}
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      width: "40px",
                      padding: "4px",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(sms.id)}
                      sx={{ color: "white", padding: "2px" }} // smaller click area
                    >
                      {expandedRows[sms.id] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {sms.id}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {new Date(sms.date).toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: "10.5px", sm: "14px", md: "15px" } }}
                  >
                    {sms.unitId}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: "10.5px", sm: "14px", md: "15px" } }}
                  >
                    {sms.to}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {sms.text}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: { xs: "10.5px", sm: "14px", md: "15px" } }}
                  >
                    {sms.status}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {sms.length ?? "pending"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    {sms.carrierName ?? "pending"}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={4}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[sms.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Message ID:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {sms.id}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Date:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {formatDate(sms.date)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Text:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {sms.text}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Length:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {sms.length}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Carrier Name:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {sms.carrierName}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                No Emails founds
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ mt: 2, mx: 0 }}>
      {loading ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={{ xs: 2, md: 0 }}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            mb={2}
          >
            <TextField
              label="Search Email..."
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 200, width: { xs: "100%", sm: 300, md: 300 } }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 1,
                width: { xs: "100%", sm: "unset", sm: "unset" },
                "& > button": {
                  flex: { xs: 1, sm: "unset" },
                },
              }}
            >
              <Button
                sx={{
                  borderRadius: 0, // ✅ remove rounding
                }}
                variant="contained"
                color="primary"
                onClick={fetchSMS}
              >
                Refresh
              </Button>
              <Button
                color="white"
                variant="outlined"
                startIcon={<GetAppIcon />}
                sx={{
                  borderRadius: 0,
                  fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                }}
                onClick={handleExportMenuOpen}
              >
                Export
              </Button>
            </Box>
          </Grid>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={copyToClipboard}>
              <ListItemIcon>
                <FileCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToCSV}>
              <ListItemIcon>
                <DescriptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToExcel}>
              <ListItemIcon>
                <TableViewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Excel</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToPDF}>
              <ListItemIcon>
                <PictureAsPdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>PDF</ListItemText>
            </MenuItem>
          </Menu>
          {renderUnitsTable()}
        </>
      )}
    </Box>
  );
};

export default EmailDelivery;
